import { canAccessInnerCircle } from '@whispers/sdk';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useCreateReading, useDecks, useSpreads } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { CardListSkeleton, Skeleton } from '../components/Skeleton.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { getMembershipUrl } from '../utils/membership.js';

export const SpreadsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Reserved for future auto-selection feature
  void searchParams.get('selected');

  const { data: spreadsData, isLoading: spreadsLoading } = useSpreads({
    include: ['positions'],
  });
  const { data: decksData, isLoading: decksLoading } = useDecks();

  const createReading = useCreateReading();

  // Navigate on successful reading creation
  const handleMutationSuccess = (data: { reading: { id: string } }) => {
    void navigate(`/readings/${data.reading.id}/draw`, { replace: true });
  };

  const spreads = spreadsData?.spreads ?? [];
  const positions = spreadsData?.positions ?? [];
  const decks = decksData?.decks ?? [];
  const handleSelectSpread = (spreadId: string, deckId: string) => {
    if (
      !canAccessInnerCircle(user) &&
      decks.find((deck) => deck.id === deckId)?.slug === 'keepers-of-the-wild-lore'
    ) {
      return;
    }
    createReading.mutate(
      { spreadId, deckId },
      { onSuccess: handleMutationSuccess, onError: () => navigate('/feeds') },
    );
  };

  if (spreadsLoading || decksLoading) {
    return (
      <>
        <div>
          <Skeleton className="mb-2 h-6 w-40" />
          <Skeleton className="h-4 w-56" />
        </div>
        <CardListSkeleton count={3} lines={3} />
      </>
    );
  }

  return (
    <>
      <div>
        <h1 className="text-xl font-medium text-text">Choose a Spread</h1>
        <p className="mt-1 text-sm text-text-secondary">Select a spread for your reading</p>
      </div>

      <div className="grid gap-4">
        {decks.map((deck) => {
          const isKeepers = deck.slug === 'keepers-of-the-wild-lore';
          const hasAccess = !isKeepers || canAccessInnerCircle(user);
          return (
            <Card key={deck.id}>
              <h2 className="text-lg font-medium text-text">{deck.name}</h2>
              {deck.description && (
                <p className="mt-1 text-sm text-text-secondary">{deck.description}</p>
              )}
              {hasAccess ? (
                <div className="mt-4 grid gap-3 grid-cols-2">
                  {spreads.map((spread) => (
                    <button
                      key={spread.id}
                      onClick={() => handleSelectSpread(spread.id, deck.id)}
                      disabled={createReading.isPending}
                      className="w-full text-left"
                    >
                      <h3 className="font-medium text-text">{spread.name}</h3>
                      <p className="mt-1 text-xs text-disabled-text">
                        {positions.filter((p) => p.spreadId === spread.id).length} cards
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-4">
                  <p className="mb-3 text-sm text-text-secondary">
                    Keepers of the Wild Lore is available to Inner Circle members.
                  </p>
                  <Button href={getMembershipUrl('inner_circle', '/spreads')}>
                    Explore Inner Circle
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {createReading.isPending && (
        <div className="fixed inset-0 flex items-center justify-center bg-shadow/20">
          <Card>
            <div className="flex items-center gap-3 px-4">
              <Spinner />
              <span>Creating reading...</span>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};
